import {Inject, Injectable} from '@angular/core';
import {FILE_CONFIG, FileConfig} from '@docma-mm-core/config/file-config';
import {BasedataService} from '@docma-mm-core/shared/basedata.service';
import {HttpClient} from '@angular/common/http';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {firstValueFrom, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {File} from './models/file';
import { Logger } from '@mydocma/common/src/lib/logger';
import { parseContentDispositionFilename } from '@mydocma/task';

export interface FetchedFile {
  blob: Blob;
  fileName: string;
  mimeType: string;
}

export interface FileMetadata {
  id: string;
  name: string;
  type: string;
  size: number;
}

@Injectable()
export class FileApiService extends BasedataService {

  constructor(private http: HttpClient, configService: ConfigService,
              @Inject(FILE_CONFIG) private fileConfig: FileConfig,
              private readonly logger: Logger) {
    super(configService);
  }

  search(subId: string): Promise<File[]> {
    const endpoint = `${this.fileConfig.apiUrl.replace(/\/+$/, '')}/metadata/search`;

    const body = {
      userId: subId,
      contentType: 'mm.DefectsExport',
      downloaded: false
    };
    return firstValueFrom(this.http.post<File[]>(endpoint, JSON.stringify(body), { headers: this.getHeaders() })
      .pipe(
        map(x => {
          return x;
        }),
        catchError((e) => {
          this.logger.error(e, '', 'FileApiService');
          return of(null);
        })
      ));
  }

  async getMetadata(id: string): Promise<FileMetadata | null> {
    const endpoint = `${this.fileConfig.apiUrl.replace(/\/+$/, '')}/metadata/${id}`;
    try {
      return await firstValueFrom(this.http.get<FileMetadata>(endpoint, { headers: this.getHeaders() }));
    } catch (e) {
      this.logger.error(e, '', 'FileApiService');
      return null;
    }
  }

  async getFile(id: string, thumbnail?: 'small' | 'medium'): Promise<FetchedFile | null> {
    const base = `${this.fileConfig.apiUrl.replace(/\/+$/, '')}/file/${id}`;
    const endpoint = thumbnail ? `${base}?defaultThumbnail=${thumbnail}` : base;
    try {
      const response = await firstValueFrom(
        this.http.get(endpoint, { headers: this.getHeaders(), observe: 'response', responseType: 'blob' })
      );
      const blob = response.body;
      const contentDisposition = response.headers.get('content-disposition');
      const fileName = contentDisposition ? parseContentDispositionFilename(contentDisposition) : '';
      return { blob, fileName, mimeType: blob?.type || '' };
    } catch (e) {
      this.logger.error(e, '', 'FileApiService');
      return null;
    }
  }

  delete(id: string) {
    const endpoint = `${this.fileConfig.apiUrl.replace(/\/+$/, '')}/file/${id}`;

    return firstValueFrom(this.http.delete(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => {
        this.logger.error(e, '', 'FileApiService');
        return of(null);
      })));
  }

}
